import { useEffect, useRef, useState } from 'react';
import { usePsychologistContext } from '../contexts/PsychologistContext';
import { useAuth } from '../contexts/AuthContext';

const RANKING_WEBHOOK_URL = 'https://hook.us1.make.com/wbr01ud7bvw6nubj50477jiscm56810w';

export function egresoAckKey(psychologistId: string) {
  return `psi-journey-egreso-ack-${psychologistId}`;
}

/**
 * Decide si la psicóloga ya puede acceder a Home o todavía está dentro del journey NZ
 * ("Tu camino inicial"). Solo puede entrar a Home si nunca fue NZ, o si se graduó y ya
 * confirmó el popup de egreso en Camino Inicial (mismo `egresoAckKey` que usa
 * ProfileMetricsTab/MetricsCaminoInicialTab).
 *
 * El webhook de ranking (`ranking_anterior`) solo se consulta cuando hace falta: si ya
 * sabemos por `rankingCategory` del perfil que sigue siendo NZ, o si ya confirmó el egreso,
 * no hay ambigüedad y nos ahorramos el fetch.
 *
 * `psychologist` del contexto solo lo populan hoy CalendarPage y PsychologistProfile — si esta
 * pantalla (Home, o el redirect de "/") es la primera en montar, nunca nadie lo carga y este hook
 * quedaría en `loading` para siempre (nunca se decide canAccessHome, nunca aparecen Pacientes).
 * Por eso lo pedimos nosotros mismos en cuanto sabemos el id, en vez de asumir que ya está.
 */
export function useNzJourneyStatus() {
  const { psychologist, loadPsychologist } = usePsychologistContext();
  const { psychologistId } = useAuth();
  const resolvedId = psychologistId || psychologist?.id || '';

  const [rankingAnterior, setRankingAnterior] = useState<string | null>(null);
  const [webhookLoading, setWebhookLoading] = useState(true);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const attemptedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (psychologist || !psychologistId) return;
    if (attemptedIdRef.current === psychologistId) return;
    attemptedIdRef.current = psychologistId;
    setProfileLoadFailed(false);
    loadPsychologist(psychologistId).catch(() => setProfileLoadFailed(true));
  }, [psychologist, psychologistId, loadPsychologist]);

  const psychologistReady = !!psychologist || profileLoadFailed;
  const isCurrentlyNZ = psychologist?.rankingCategory === 'NZ';
  const egresoAck = resolvedId ? localStorage.getItem(egresoAckKey(resolvedId)) === 'true' : false;

  useEffect(() => {
    let cancelled = false;

    if (!psychologistReady || !resolvedId || isCurrentlyNZ || egresoAck) {
      setWebhookLoading(false);
      return () => { cancelled = true; };
    }

    setWebhookLoading(true);
    fetch(`${RANKING_WEBHOOK_URL}?psychologist_id=${resolvedId}`)
      .then((r) => r.json())
      .then((data: { ranking_anterior: string }) => {
        if (!cancelled) setRankingAnterior(data.ranking_anterior);
      })
      .catch(() => { /* ante error, se asume que no viene de NZ para no bloquear Home de más */ })
      .finally(() => { if (!cancelled) setWebhookLoading(false); });

    return () => { cancelled = true; };
  }, [resolvedId, psychologistReady, isCurrentlyNZ, egresoAck]);

  const loading = !psychologistReady || webhookLoading;
  const isPendingGraduationConfirm = !isCurrentlyNZ && !egresoAck && rankingAnterior === 'NZ';
  const canAccessHome = !loading && !isCurrentlyNZ && !isPendingGraduationConfirm;

  return { loading, canAccessHome, egresoAck, isCurrentlyNZ };
}
